import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { UserProfileService } from '../services/user-profile.service';
import { TrialExpiredDialogComponent } from 'src/app/shared/components/trial-expired-dialog/trial-expired-dialog.component';

/**
 * Soft-block guard.
 *
 * Scope: applied only to the "new test" route. When the current user's
 * phase has expired (Trial / Test / Production — whichever they are
 * currently on), we cancel the navigation and pop a small dialog
 * inviting them to contact sales. The block clears as soon as a Super
 * User renews / changes their phase.
 *
 * Every other part of the app — dashboard, test list, reports, settings —
 * stays fully reachable so the user can still review their data and
 * manage their account.
 *
 * If the profile fetch fails the guard is permissive (true). We never want
 * a transient API failure to block a user from running a test.
 */
export const blockedAccessGuard: CanActivateFn = (): Observable<boolean> => {
  const userProfileService = inject(UserProfileService);
  const dialogService = inject(DialogService);

  return userProfileService.getUserProfile().pipe(
    map(profile => {
      const phaseExpired =
        profile?.currentPhase?.isExpired === true || profile?.testPhase?.status === 'Expired';
      if (phaseExpired) {
        dialogService.open(TrialExpiredDialogComponent, {
          showHeader: false,
          dismissableMask: true,
          styleClass: 'trial-expired-dialog',
          width: '480px',
        });
        return false;
      }
      return true;
    }),
    catchError(() => of(true))
  );
};
